import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, ShieldCheck, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../api/client";
import * as activationsApi from "../../api/activations";
import { Attivazione } from "../../api/activations";
import * as anagraficaApi from "../../api/anagrafica";
import { IstitutoVigilanzaOption, CentroAssistenzaOption } from "../../api/anagrafica";
import { TipoDispositivo } from "../../types";
import { Table, TableColumn } from "../../components/ui/Table";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { SlideOver } from "../../components/ui/SlideOver";
import {
  RiferimentiOdoo,
  ValoriRiferimentiOdoo,
  riferimentiOdooDaRecord,
  payloadRiferimentiOdoo,
  senzaRiferimentiOdoo,
  usaPuoGestireRiferimentiOdoo,
} from "../../components/ui/RiferimentiOdoo";
import { MediaUploader } from "../../components/ui/MediaUploader";
import { ExportButton } from "../../components/ui/ExportButton";
import { ScheduleAutocomplete } from "../../components/ui/ScheduleAutocomplete";
import * as scheduleApi from "../../api/schedule";
import { AssegnatarioSelect, useCentroDelChiamante } from "../../components/ui/AssegnatarioSelect";
import { SedeAutocomplete } from "../../components/ui/SedeAutocomplete";
import { InstallationSchedule } from "../../types";
import { ClienteAutocomplete } from "../../components/ui/ClienteAutocomplete";
import { DispositivoAutocomplete } from "../../components/ui/DispositivoAutocomplete";
import { DeviceFisicoAutocomplete } from "../../components/ui/DeviceFisicoAutocomplete";
import { CensimentoDevice } from "../../components/ui/CensimentoDevice";
import { modelloDaDevice } from "../../utils/modelloDaDevice";
import { TipoPossessoDevice } from "../../api/devices";
import { DispositivoMateriale } from "../../components/ui/DispositivoMateriale";
import { CampiOrario } from "../../components/ui/CampiOrario";
import { orarioPerInput } from "../../utils/orario";
import { sectionFlatStyle, sectionTileStyle } from "../../utils/sectionColors";
import { MeseSelect } from "../../components/ui/MeseSelect";
import { RitardoBanner } from "../../components/ui/RitardoBanner";
import { useMessaggioListaVuota } from "../../components/ui/ListaVuota";
import { AvvisoBozza, StatoBozzaNelForm, confermaDopoIlSalvataggio, leggiBozzaPerForm } from "../StatoLavori/BozzaNelForm";
import { bozzaDalLink } from "../StatoLavori/presentazione";
import { meseDaUrl, useOrdinamentoUrl, usePannelloRecord, useRicercaUrl, useScriviParametri } from "../../hooks/useStatoUrl";

const TIPI_DISPOSITIVO: TipoDispositivo[] = ["Cassaforte", "Rendiresto", "TVCC", "Allarme", "Altro"];
const DEVICE_TYPES_WITH_COMUNICAZIONE = ["Cassaforte", "Allarme"];
const COMUNICAZIONE_TIPI = ["LAN", "SIM", "Nessuna"] as const;
const TIPI_ATTIVAZIONE = ["Prima attivazione", "Riattivazione"];
const STATI_ATTIVAZIONE = ["Attiva", "Sospesa", "Cessata"];
// Due sole opzioni, come richiesto — "congiunto" richiede di indicare CON
// QUALE centro, "autonoma" no (è solo assistenza telefonica, nessun centro coinvolto).
const TIPI_SERVIZIO_VIGILANZA = ["Autonoma (assistenza telefonica)", "Congiunto con assistenza"];

type Modalita = "autonoma" | "collegata";

type FormValues = {
  schedule_id: string;
  centro_assistenza_id: string;
  /** Anagrafica cliente collegata (FK). Distinto da cliente_nome (testo/insegna). */
  cliente_id: string;
  cliente_nome: string;
  sede: string;
  sede_id: string;
  tipo_dispositivo: string;
  marca: string;
  modello: string;
  dispositivo_matricola: string;
  dispositivo_catalogo_id: string;
  data_attivazione: string;
  orario_inizio: string;
  orario_fine: string;
  effettuata: boolean;
  data_effettiva: string;
  tipo_attivazione: string;
  vigilanza: string;
  istituto_vigilanza_id: string;
  tipo_servizio: string;
  centro_assistenza_vigilanza_id: string;
  comunicazione_tipo: string;
  lan_dhcp: boolean;
  lan_ip: string;
  lan_subnet: string;
  lan_gateway: string;
  lan_dns: string;
  lan_dns2: string;
  sim_numero: string;
  sim_operatore: string;
  tecnico_attivazione: string;
  assegnato_utente_id: string; // persona incaricata; "" = nessuna
  stato: string;
  note: string;
  // I cinque riferimenti OdA/OdV stanno DENTRO il form e non in uno `useState`
  // accanto: così `reset()` li riallinea aprendo un altro record, che è
  // esattamente il difetto annotato più sotto per la spunta del censimento —
  // quella vive fuori dal form e va azzerata a mano.
} & ValoriRiferimentiOdoo;

function toFormValues(record?: Attivazione): FormValues {
  return {
    schedule_id: record?.schedule_id ?? "",
    centro_assistenza_id: record?.centro_assistenza_id ?? "",
    cliente_id: record?.cliente_id ?? "",
    cliente_nome: record?.cliente_nome ?? "",
    sede: record?.sede ?? "",
    sede_id: record?.sede_id ?? "",
    tipo_dispositivo: record?.tipo_dispositivo ?? "",
    marca: record?.marca ?? "",
    modello: record?.modello ?? "",
    dispositivo_matricola: record?.dispositivo_matricola ?? "",
    dispositivo_catalogo_id: record?.dispositivo_catalogo_id ?? "",
    data_attivazione: record?.data_attivazione?.slice(0, 10) ?? "",
    orario_inizio: orarioPerInput(record?.orario_inizio),
    orario_fine: orarioPerInput(record?.orario_fine),
    effettuata: record?.effettuata ?? false,
    data_effettiva: record?.data_effettiva?.slice(0, 10) ?? "",
    tipo_attivazione: record?.tipo_attivazione ?? "Prima attivazione",
    vigilanza: record?.vigilanza ?? "",
    istituto_vigilanza_id: record?.istituto_vigilanza_id ?? "",
    tipo_servizio: record?.tipo_servizio ?? "",
    centro_assistenza_vigilanza_id: record?.centro_assistenza_vigilanza_id ?? "",
    comunicazione_tipo: record?.comunicazione_tipo ?? "Nessuna",
    lan_dhcp: record?.lan_dhcp ?? false,
    lan_ip: record?.lan_ip ?? "",
    lan_subnet: record?.lan_subnet ?? "",
    lan_gateway: record?.lan_gateway ?? "",
    lan_dns: record?.lan_dns ?? "",
    lan_dns2: record?.lan_dns2 ?? "",
    sim_numero: record?.sim_numero ?? "",
    sim_operatore: record?.sim_operatore ?? "",
    tecnico_attivazione: record?.tecnico_attivazione ?? "",
    assegnato_utente_id: record?.assegnato_utente_id ?? "",
    stato: record?.stato ?? "Attiva",
    note: record?.note ?? "",
    ...riferimentiOdooDaRecord(record),
  };
}

export default function Activations() {
  const { user, hasPermission } = useAuth();
  const { showToast } = useToast();
  // La chiave del device NON viene inviata: questa rotta deriva `device_id`
  // dalla matricola, e aggiungere un secondo percorso creerebbe due scritture
  // che possono divergere. Serve solo al selettore per sapere se la matricola
  // in campo viene da una scelta o e' stata digitata.
  const [deviceSceltoId, setDeviceSceltoId] = useState("");
  // Spunta "censisci il dispositivo" e possesso dichiarato accanto ad essa.
  // Serve al caso piu' frequente descritto nella segnalazione: il pezzo e' gia'
  // installato fisicamente e a noi tocca solo l'Attivazione, ma se non e'
  // censito l'attivazione non ha niente a cui agganciarsi.
  const [censisciDevice, setCensisciDevice] = useState(false);
  const [possessoCensimento, setPossessoCensimento] = useState<TipoPossessoDevice | "">("");

  /**
   * Azzera lo stato del censimento fra un'apertura e l'altra del pannello: vive
   * fuori dal form, quindi `reset()` non lo tocca, e una spunta dimenticata
   * censirebbe una matricola che nessuno ha guardato.
   */
  function azzeraCensimento(deviceId = "") {
    setDeviceSceltoId(deviceId);
    setCensisciDevice(false);
    setPossessoCensimento("");
  }
  const isAdmin = user?.role === "admin";
  // I riferimenti OdA/OdV sono di amministrazione: li vedono admin e staff, non i
  // Centri Assistenza né gli Istituti di Vigilanza, che pure possono modificare
  // un'attivazione. La regola sta nell'hook e non qui, perché la condividono
  // cinque form — vedi `usaPuoGestireRiferimentiOdoo`.
  const puoGestireOrdini = usaPuoGestireRiferimentiOdoo();
  // Un CA in modalità autonoma non sceglie il centro: il campo resta vuoto e il
  // backend lo forza al suo. Per l'elenco "Assegnato a" vale già il suo. Non in
  // modalità collegata, dove il backend salva il centro a null.
  const centroDelChiamante = useCentroDelChiamante();

  const [searchParams] = useSearchParams();
  const ritardo = searchParams.get("ritardo") === "true";
  // Mese, ricerca, ordinamento e attivazione aperta stanno nell'URL come
  // `ritardo` (vedi `hooks/useStatoUrl.ts`): tornando indietro, o aprendo un
  // link, si ritrova la stessa vista.
  const scrivi = useScriviParametri();
  const { ordine, onOrdineChange } = useOrdinamentoUrl();

  const [records, setRecords] = useState<Attivazione[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const mese = meseDaUrl(searchParams);
  const setMese = (m: number | undefined) => scrivi({ mese: m ? String(m) : null });
  const { testo: ricerca, setTesto: setRicerca, valore: ricercaAttiva } = useRicercaUrl();
  const messaggioVuoto = useMessaggioListaVuota(
    "Nessuna attivazione trovata.",
    // Anche la ricerca conta come filtro attivo: senza, l'elenco vuoto direbbe
    // "non ce ne sono" invece di "nessuna corrisponde a quello che hai scritto".
    Boolean(ritardo || mese !== undefined || ricercaAttiva)
  );
  const [editingRecord, setEditingRecord] = useState<Attivazione | null>(null);
  const [modalita, setModalita] = useState<Modalita>("autonoma");
  const [schedaSelezionata, setSchedaSelezionata] = useState<InstallationSchedule | null>(null);
  // La bozza di Stato lavori da cui si apre «Nuova attivazione» (`&bozza=<id>`, PR C2).
  const [bozzaNelForm, setBozzaNelForm] = useState<StatoBozzaNelForm | null>(null);
  // Finché la bozza si legge il form resta fermo: il precompilato riscrive i suoi campi.
  const bozzaInCaricamento = bozzaNelForm?.stato === "caricamento";
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);

  const [istituti, setIstituti] = useState<IstitutoVigilanzaOption[]>([]);
  const [centri, setCentri] = useState<CentroAssistenzaOption[]>([]);

  useEffect(() => {
    anagraficaApi.listIstitutiVigilanza().then(({ istituti }) => setIstituti(istituti));
    if (isAdmin) {
      anagraficaApi.listCentriAssistenza().then(({ centri }) => setCentri(centri));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadRecords() {
    setIsLoading(true);
    try {
      // mese/anno e ritardo filtrati lato server.
      const { records: list } = await activationsApi.listAttivazioni({
        mese,
        ritardo: ritardo || undefined,
        search: ricercaAttiva || undefined,
      });
      setRecords(list);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare le attivazioni."), "error");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mese, ritardo, ricercaAttiva]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm<FormValues>({ defaultValues: toFormValues() });

  const tipoDispositivo = watch("tipo_dispositivo");
  const comunicazioneTipo = watch("comunicazione_tipo");
  const showComunicazione = DEVICE_TYPES_WITH_COMUNICAZIONE.includes(tipoDispositivo);
  const isEffettuata = watch("effettuata");
  const isCongiunto = watch("tipo_servizio") === "Congiunto con assistenza";
  const isDhcp = watch("lan_dhcp");

  // Dai permessi reali, non da un elenco di ruoli scritto qui: erano due liste
  // che dicevano cose diverse, e staff — che il backend accetta su create e
  // update — vedeva la pagina col form disabilitato e senza il bottone Salva.
  // La richiesta non partiva nemmeno, quindi non c'era un 403 da cui accorgersi.
  const canCreate = hasPermission("attivazioni:create");
  const canEdit = hasPermission("attivazioni:update");

  /**
   * Prepara il modulo di creazione; il pannello lo apre `?new=1`.
   *
   * Con `&schedule=<id>` arriva dal promemoria "Da attivare il servizio Pre
   * Credit" di un'installazione: il form si apre già in modalità "collegata" e
   * precompilato, riusando lo stesso handleScheduleSelected dell'autocomplete.
   * Passare per l'id invece che per i campi tiene una sola definizione di
   * "cosa eredita un'attivazione da un'installazione".
   */
  function openCreate() {
    setEditingRecord(null);
    setModalita("autonoma");
    setSchedaSelezionata(null);
    reset(toFormValues());
    azzeraCensimento();
    const daBozza = bozzaDalLink(window.location.search);
    setBozzaNelForm(daBozza ? { stato: "caricamento", ...daBozza } : null);
    if (daBozza) {
      precompilaDaBozza(daBozza.bozzaId, daBozza.praticaId);
      return;
    }
    const scheduleId = new URLSearchParams(window.location.search).get("schedule");
    if (scheduleId) {
      setModalita("collegata");
      scheduleApi
        .getSchedule(scheduleId)
        .then(({ record }) => handleScheduleSelected(record))
        // Se la scheda non è raggiungibile (cancellata, o fuori dallo scope di
        // chi ha aperto il link) il form resta aperto e vuoto: si compila a
        // mano. Bloccarlo sarebbe peggio del collegamento mancante.
        .catch(() => undefined);
    }
  }

  /**
   * Da una bozza di Stato lavori (`&bozza=<id>&pratica=<id>`, PR C2): collegata
   * all'installazione della bozza, come il passaggio `&schedule=<id>`, ma con
   * il cliente e la sede della PRATICA. Su una cassaforte condivisa
   * l'installazione ha un cliente solo, e la bozza è di ciascun cliente.
   */
  function precompilaDaBozza(bozzaId: string, praticaId: string | null) {
    const ancoraQui = () => bozzaDalLink(window.location.search)?.bozzaId === bozzaId;
    leggiBozzaPerForm(bozzaId)
      .then(async (letta) => {
        if (!ancoraQui()) return;
        if (!letta) {
          setBozzaNelForm({ stato: "non_valida", bozzaId, praticaId });
          return;
        }
        const { bozza, sede, device } = letta;
        if (bozza.dati.schedule_id) {
          const scheda = await scheduleApi.getSchedule(bozza.dati.schedule_id).then((r) => r.record).catch(() => null);
          if (!ancoraQui()) return;
          if (scheda) {
            setModalita("collegata");
            handleScheduleSelected(scheda);
          }
        }
        setValue("cliente_id", bozza.dati.cliente_id);
        setValue("cliente_nome", sede?.cliente?.nome ?? "");
        setValue("sede_id", bozza.dati.sede_id);
        setValue("sede", sede?.nome ?? "");
        if (device) {
          // Dalla matricola il backend ricava il device: è quella della cassaforte.
          setValue("dispositivo_matricola", device.matricola ?? "");
          const { marca, modello, catalogoId } = modelloDaDevice(device);
          if (marca !== null) setValue("marca", marca);
          if (modello !== null) setValue("modello", modello);
          setValue("dispositivo_catalogo_id", catalogoId ?? "");
          azzeraCensimento(device.id);
        }
        setBozzaNelForm({ stato: "valida", bozzaId, praticaId, letta });
      })
      .catch((err) => {
        if (ancoraQui()) setBozzaNelForm({ stato: "errore", bozzaId, praticaId, messaggio: getErrorMessage(err, "errore di rete.") });
      });
  }

  function openEdit(record: Attivazione) {
    setEditingRecord(record);
    setBozzaNelForm(null);
    // "collegata" se legata a una scheda del Calendario, che dopo il ritiro del
    // Modulo Installazioni è l'unica fonte possibile.
    setModalita(record.schedule_id ? "collegata" : "autonoma");
    setSchedaSelezionata(null);
    reset(toFormValues(record));
    azzeraCensimento(record.device_id ?? "");
  }

  // Pannello su `?open=<id>` (clic su una riga, Calendario unificato, scheda
  // cliente) e su `?new=1` (pulsante, azione rapida della Dashboard,
  // promemoria di un'installazione con `&schedule=<id>`).
  const pannello = usePannelloRecord({
    records,
    isLoading,
    preparaNuovo: openCreate,
    preparaModifica: openEdit,
    chiaviCollegate: ["schedule", "bozza", "pratica"],
  });
  const panelOpen = pannello.aperto;

  /**
   * Selezionando una scheda del Calendario (installation_schedule, fonte reale),
   * precompila i campi che altrimenti si ridigiterebbero a mano. La scheda ha
   * `macchina` (marca+modello insieme) invece di marca/modello separati, quindi
   * quelli restano da compilare a mano se servono.
   */
  function handleScheduleSelected(scheda: InstallationSchedule) {
    setSchedaSelezionata(scheda);
    setValue("schedule_id", scheda.id);
    setValue("cliente_id", scheda.cliente_id ?? "");
    setValue("cliente_nome", scheda.insegna ?? "");
    setValue("sede", scheda.localita ?? "");
    setValue("sede_id", scheda.sede_id ?? "");
    setValue("tipo_dispositivo", scheda.tipo_dispositivo ?? "");
  }

  const onSubmit = handleSubmit(async (values) => {
    const payload: Partial<Attivazione> = {
      // Senza i cinque campi OdA/OdV, che si aggiungono piu' sotto e solo se cambiati.
      ...senzaRiferimentiOdoo(values),
      cliente_id: values.cliente_id || null, // "" -> null (colonna @db.Uuid)
      sede_id: values.sede_id || null,
      schedule_id: modalita === "collegata" ? values.schedule_id || null : null,
      // In modalità collegata il backend salva il centro a null, e per un CA
      // `puoAssegnare` rifiuta qualunque assegnatario (#249): un valore rimasto
      // nel form farebbe fallire il salvataggio, quindi non si manda.
      assegnato_utente_id:
        modalita === "collegata" && user?.role === "centro_assistenza"
          ? undefined
          : values.assegnato_utente_id || null,
      // Tutti i FK UUID vanno normalizzati "" -> null (Prisma rifiuta la stringa
      // vuota su @db.Uuid). Il centro si sceglie solo in modalità autonoma da admin.
      centro_assistenza_id: (isAdmin && modalita === "autonoma" ? values.centro_assistenza_id : "") || null,
      istituto_vigilanza_id: values.istituto_vigilanza_id || null,
      data_attivazione: values.data_attivazione || null,
      orario_inizio: values.orario_inizio || null,
      orario_fine: values.orario_fine || null,
      data_effettiva: values.effettuata ? values.data_effettiva || null : null,
      centro_assistenza_vigilanza_id: isCongiunto ? values.centro_assistenza_vigilanza_id || null : null,
      dispositivo_catalogo_id: values.dispositivo_catalogo_id || null,
      comunicazione_tipo: showComunicazione
        ? (values.comunicazione_tipo as Attivazione["comunicazione_tipo"])
        : "Nessuna",
      // I cinque riferimenti OdA/OdV, tradotti dalla forma del form a quella del
      // database (numero vuoto -> null, garanzia non specificata -> null).
      // Inviati solo se il riquadro era visibile: per gli altri ruoli il backend
      // li scarterebbe comunque, e mandare cinque null che verranno ignorati
      // rende più difficile leggere cosa il form ha davvero chiesto. E solo i
      // CAMBIATI: fattura e garanzia sono di un ordine che può coprire altre
      // attività (vedi `payloadRiferimentiOdoo`).
      ...(puoGestireOrdini ? payloadRiferimentiOdoo(values, riferimentiOdooDaRecord(editingRecord)) : {}),
    } as Partial<Attivazione>;

    if (!showComunicazione) {
      payload.lan_dhcp = false;
      payload.lan_ip = payload.lan_subnet = payload.lan_gateway = payload.lan_dns = payload.lan_dns2 = null;
      payload.sim_numero = payload.sim_operatore = null;
    } else if (values.comunicazione_tipo === "LAN" && values.lan_dhcp) {
      payload.lan_ip = payload.lan_subnet = payload.lan_gateway = payload.lan_dns = payload.lan_dns2 = null;
    }

    // Le due chiavi del censimento NON sono colonne di `attivazioni`: la rotta
    // le legge dal body grezzo e le usa per decidere se creare il pezzo fisico.
    // Il controllo del permesso `devices:create` sta li', questa e' la richiesta.
    const daInviare: activationsApi.AttivazionePayload = {
      ...payload,
      censisci_device: censisciDevice,
      censimento_tipo_possesso: possessoCensimento || null,
    };

    try {
      const base = editingRecord ? "Attivazione aggiornata." : "Attivazione creata.";
      const { record, censimento } = editingRecord
        ? await activationsApi.updateAttivazione(editingRecord.id, daInviare)
        : await activationsApi.createAttivazione(daInviare);
      // Da una bozza (PR C2): l'attivazione la conferma. Se nel frattempo la
      // bozza non è più valida, l'attivazione resta salvata e lo si dice.
      const daBozza = !editingRecord && bozzaNelForm?.stato === "valida" ? bozzaNelForm : null;
      if (daBozza) {
        const avviso = await confermaDopoIlSalvataggio(daBozza.bozzaId, record.id);
        showToast(avviso ? `${base} La bozza non risulta confermata: ${avviso}` : `${base} Bozza confermata.`, avviso ? "info" : "success");
        if (daBozza.praticaId) {
          navigate(`/stato-lavori/${daBozza.praticaId}`);
          return;
        }
        pannello.chiudi();
        loadRecords();
        return;
      }

      // L'esito del censimento si dichiara: un "salvato" che tace su un device
      // creato lascia chi salva senza sapere se il parco e' stato toccato.
      showToast(
        censimento?.creato ? `${base} Dispositivo ${censimento.matricola} censito e collegato.` : base,
        "success"
      );
      pannello.chiudi();
      loadRecords();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile salvare l'attivazione."), "error");
    }
  });

  async function handleDelete() {
    if (!editingRecord) return;
    const etichetta = editingRecord.cliente_nome ?? "senza insegna";
    const ok = window.confirm(
      `Eliminare DEFINITIVAMENTE l'attivazione di ${etichetta}?\n\nL'operazione non è reversibile. Se ha allegati collegati verrà bloccata.`
    );
    if (!ok) return;

    setIsDeleting(true);
    try {
      await activationsApi.deleteAttivazione(editingRecord.id);
      showToast("Attivazione eliminata.", "success");
      pannello.chiudi();
      loadRecords();
    } catch (err) {
      // Il backend risponde 409 con `dettaglio`: dire QUANTI allegati bloccano
      // è la differenza fra "non si può" e sapere cosa rimuovere per procedere.
      const dettaglio = (err as { response?: { data?: { dettaglio?: Record<string, number> } } }).response?.data
        ?.dettaglio;
      if (dettaglio) {
        const parti = Object.entries(dettaglio)
          .filter(([, n]) => n > 0)
          .map(([k, n]) => `${k.replace(/_/g, " ")}: ${n}`);
        showToast(`Impossibile eliminare: ci sono allegati collegati (${parti.join(", ")}).`, "error");
      } else {
        showToast(getErrorMessage(err, "Impossibile eliminare l'attivazione."), "error");
      }
    } finally {
      setIsDeleting(false);
    }
  }

  const columns: TableColumn<Attivazione>[] = [
    {
      key: "cliente_nome",
      header: "Insegna",
      // Identifica la riga: senza, resta un elenco di stati senza sapere di chi.
      sempreVisibile: true,
      sortValue: (r) => r.cliente_nome,
      render: (r) => r.cliente_nome ?? "—",
    },
    { key: "sede", header: "Sede", sortValue: (r) => r.sede, render: (r) => r.sede ?? "—" },
    {
      key: "tipo_dispositivo",
      header: "Dispositivo",
      sortValue: (r) => r.tipo_dispositivo,
      render: (r) => r.tipo_dispositivo ?? "—",
    },
    {
      key: "vigilanza",
      header: "Istituto Vigilanza",
      // Spenta di default, e NON per scarsità di dati: in produzione è 4 su 4.
      // È che con sette colonne su una tabella stretta qualcosa deve cedere, e
      // questa è l'unica che non serve a distinguere una riga dall'altra —
      // l'istituto si ripete, l'insegna no. Chi lavora per istituto la riaccende
      // una volta e se la ritrova.
      nascostaDiDefault: true,
      sortValue: (r) => r.vigilanza,
      render: (r) => r.vigilanza ?? "—",
    },
    {
      key: "effettuata",
      header: "Effettuata",
      // "Sì" prima di "No" in ordine crescente: raggruppa, che è l'uso vero.
      sortValue: (r) => (r.effettuata ? "Sì" : "No"),
      render: (r) => (r.effettuata ? <Badge color="success">Sì</Badge> : <Badge color="neutral">No</Badge>),
    },
    {
      key: "stato",
      header: "Stato",
      // Sullo stato principale soltanto: "In ritardo" e "Senza data" sono
      // segnalazioni che si aggiungono, non valori alternativi, e infilarle nel
      // criterio darebbe un ordine che dalla colonna non si rilegge.
      sortValue: (r) => r.stato,
      render: (r) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge color={r.stato === "Attiva" ? "success" : "neutral"}>{r.stato}</Badge>
          {r.in_ritardo && <Badge color="danger">In ritardo</Badge>}
          {/* Un record senza date compare in ogni mese: senza dirlo, sembrerebbe
              programmato proprio nel mese che si sta guardando. */}
          {!r.data_attivazione && !r.data_effettiva && <Badge color="warning">Senza data</Badge>}
        </div>
      ),
    },
    {
      key: "dispositivo_matricola",
      header: "Matricola",
      nascostaDiDefault: true,
      sortValue: (r) => r.dispositivo_matricola,
      render: (r) => r.dispositivo_matricola ?? <span className="text-ink/30">—</span>,
    },
    {
      key: "data_attivazione",
      header: "Data attivazione",
      // Spente entrambe non perché vuote — in produzione sono 4 su 4 — ma
      // perché la colonna Stato riassume già ciò che se ne ricava a colpo
      // d'occhio, compreso il badge "Senza data". Servono quando si cerca un
      // periodo preciso, ed è allora che si accendono.
      nascostaDiDefault: true,
      // La stringa ISO si ordina già cronologicamente: nessuna conversione.
      sortValue: (r) => r.data_attivazione,
      render: (r) =>
        r.data_attivazione ? (
          new Date(r.data_attivazione).toLocaleDateString("it-IT")
        ) : (
          <span className="text-ink/30">—</span>
        ),
    },
    {
      key: "data_effettiva",
      header: "Data effettiva",
      nascostaDiDefault: true,
      sortValue: (r) => r.data_effettiva,
      render: (r) =>
        r.data_effettiva ? (
          new Date(r.data_effettiva).toLocaleDateString("it-IT")
        ) : (
          <span className="text-ink/30">—</span>
        ),
    },
    {
      key: "note",
      header: "Note",
      nascostaDiDefault: true,
      sortValue: (r) => r.note,
      render: (r) =>
        r.note ? (
          <span className="line-clamp-2 text-xs text-ink/70">{r.note}</span>
        ) : (
          <span className="text-ink/30">—</span>
        ),
    },
    {
      // La riga era già cliccabile, ma senza nulla che lo dichiarasse: su
      // un'attivazione autonoma — che non compare nel Calendario e non ha quindi
      // altre vie d'accesso — l'unico modo di aprirla era indovinare il click.
      key: "azioni",
      header: "Azioni",
      // Colonna di comandi: spenta, la pagina perde l'unico appiglio esplicito
      // per aprire una scheda, e non resta niente che spieghi come riaccenderla.
      sempreVisibile: true,
      render: (r) => (
        <Button
          size="sm"
          variant="secondary"
          onClick={(e) => {
            e.stopPropagation(); // la riga apre già lo stesso pannello
            pannello.apri(r.id);
          }}
        >
          <Pencil className="h-4 w-4" aria-hidden="true" />
          {canEdit ? "Modifica" : "Dettagli"}
        </Button>
      ),
    },
  ];

  return (
    <div>
      <header className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div style={sectionFlatStyle("attivazioni")} className="flex h-11 w-11 items-center justify-center rounded-xl">
            <ShieldCheck className="h-5 w-5" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-ink">Attivazioni</h1>
            <p className="mt-1 text-sm text-ink/50">Configurazione rete e collegamento all'istituto di vigilanza.</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Input
            label=""
            placeholder="Cerca cliente, sede o matricola…"
            value={ricerca}
            onChange={(e) => setRicerca(e.target.value)}
            className="min-w-[240px]"
          />
          <MeseSelect value={mese} onChange={setMese} />
          <ExportButton
            endpoint="/attivazioni/export"
            filename="attivazioni.xlsx"
            fields={[
              { key: "stato", label: "Stato", type: "select", options: STATI_ATTIVAZIONE },
              { key: "tipo_dispositivo", label: "Tipo dispositivo", type: "select", options: TIPI_DISPOSITIVO },
              { key: "data_da", label: "Data programmata da", type: "date" },
              { key: "data_a", label: "Data programmata a", type: "date" },
            ]}
          />
          {canCreate && (
            <Button
              onClick={() => pannello.apriNuovo()}
              style={sectionTileStyle("attivazioni")}
              className="active:translate-y-0 active:shadow-none"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Nuova attivazione
            </Button>
          )}
        </div>
      </header>

      {ritardo && <RitardoBanner onRemove={() => scrivi({ ritardo: null }, { push: true })} />}

      <Table
        columns={columns}
        data={records}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        emptyMessage={messaggioVuoto}
        onRowClick={(r) => pannello.apri(r.id)}
        ordinabile
        ordine={ordine}
        onOrdineChange={onOrdineChange}
        sceltaColonne="attivazioni"
      />

      <SlideOver
        open={panelOpen}
        onClose={pannello.chiudi}
        title={editingRecord ? "Modifica attivazione" : "Nuova attivazione"}
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            {/* Solo in modifica e solo per admin: su un record nuovo non c'è
                nulla da eliminare, e il backend rifiuta comunque gli altri ruoli. */}
            {editingRecord && isAdmin ? (
              <Button type="button" variant="danger" onClick={handleDelete} loading={isDeleting}>
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                Elimina
              </Button>
            ) : (
              <span />
            )}

            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={pannello.chiudi}>
                {canEdit ? "Annulla" : "Chiudi"}
              </Button>
              {canEdit && (
                <Button type="submit" form="activation-form" loading={isSubmitting} disabled={bozzaInCaricamento}>
                  Salva
                </Button>
              )}
            </div>
          </div>
        }
      >
        <fieldset disabled={!canEdit || bozzaInCaricamento} className="contents">
          <form id="activation-form" onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
            {!editingRecord && bozzaNelForm && <AvvisoBozza stato={bozzaNelForm} />}
            {!editingRecord && (
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-ink">Tipo attivazione</label>
                <select
                  value={modalita}
                  onChange={(e) => {
                    setModalita(e.target.value as Modalita);
                    // L'assegnatario scelto valeva per l'altro proprietario. Se
                    // il nuovo elenco risponde 403 il campo si spegne ma non si
                    // svuota da solo, e il valore partirebbe col salvataggio.
                    setValue("assegnato_utente_id", "");
                  }}
                  className="h-10 rounded-lg border border-black/10 bg-white px-3 text-sm text-ink"
                >
                  <option value="autonoma">Attivazione autonoma</option>
                  <option value="collegata">Collegata a installazione</option>
                </select>
              </div>
            )}

            {modalita === "collegata" ? (
              <ScheduleAutocomplete
                label="Installazione (dal Calendario)"
                displayValue={
                  schedaSelezionata
                    ? `${schedaSelezionata.insegna ?? "—"} · ${schedaSelezionata.localita ?? "—"}`
                    : watch("cliente_nome")
                      ? `${watch("cliente_nome")} · ${watch("sede")}`
                      : ""
                }
                onSelect={handleScheduleSelected}
                onClear={() => setValue("schedule_id", "")}
              />
            ) : (
              isAdmin && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-ink">Centro Assistenza</label>
                  <select
                    className="h-10 rounded-lg border border-black/10 bg-white px-3 text-sm text-ink"
                    {...register("centro_assistenza_id")}
                  >
                    <option value="">—</option>
                    {centri.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nome}
                      </option>
                    ))}
                  </select>
                </div>
              )
            )}

            <ClienteAutocomplete
              label="Cliente"
              value={watch("cliente_nome")}
              onChange={(v) => {
                // Digitazione manuale: testo libero, nessun collegamento all'anagrafica.
                setValue("cliente_nome", v);
                setValue("cliente_id", "");
              }}
              onSelect={(c) => {
                // Selezione dall'anagrafica: collega cliente_id (serve allo scoping cliente).
                setValue("cliente_id", c.id);
                setValue("cliente_nome", c.insegna ?? c.nome);
              }}
            />
            <SedeAutocomplete
              label="Sede"
              value={watch("sede")}
              onChange={(v) => setValue("sede", v)}
              sedeId={watch("sede_id")}
              onSedeIdChange={(v) => setValue("sede_id", v)}
              onSelect={(s) => {
                // Una sede appartiene a un'azienda madre, e l'attivazione ha
                // bisogno di quel collegamento: senza `cliente_id` non entra
                // nello scoping per cliente. Prima andava scelto due volte —
                // la sede qui, il cliente nel campo sopra — pur essendo un
                // dato che la sede porta con sé. Stessa eredità che
                // Sopralluoghi applica già.
                //
                // SOLO IN CREAZIONE. In modifica la PATCH scarta
                // `cliente_id` di proposito («quei collegamenti si
                // stabiliscono solo alla creazione», vedi la rotta) ma
                // salverebbe `cliente_nome`: resterebbe un'attivazione che
                // mostra un nome e ne collega un altro. Meglio non
                // precompilare che precompilare a metà.
                if (editingRecord) return;

                // Solo se il Cliente non è ancora stato toccato — né scelto
                // dall'anagrafica (`cliente_id`) né digitato a mano
                // (`cliente_nome`). È la stessa cautela di Sopralluoghi, che
                // eredita solo a cliente mancante; lì basta guardare la
                // chiave perché il testo non lo scrive, qui i campi sono due
                // e vanno guardati entrambi.
                if (watch("cliente_id") || watch("cliente_nome").trim()) return;

                setValue("cliente_id", s.cliente_id);
                // L'insegna prima della ragione sociale: è la regola che
                // questo campo segue già sia scegliendo dall'anagrafica
                // (`c.insegna ?? c.nome`) sia arrivando da una scheda del
                // Calendario (`scheda.insegna`). Riempirlo con la ragione
                // sociale avrebbe reso lo stesso cliente due testi diversi a
                // seconda di come lo si è scelto.
                setValue("cliente_nome", s.cliente_insegna ?? s.cliente_nome);
              }}
            />

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink">Tipo dispositivo</label>
              <select
                className="h-10 rounded-lg border border-black/10 bg-white px-3 text-sm text-ink"
                {...register("tipo_dispositivo")}
              >
                <option value="">—</option>
                {TIPI_DISPOSITIVO.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            {/* UN campo solo, come in Installazioni e come nella scheda
                Dispositivo. Prima erano due, «Marca» e «Modello», e non si
                parlavano: con «YOUGO» scritto nella Marca, il primo
                suggerimento del Modello poteva essere di un'altra marca —
                riprodotto il 31/08/2026 con «GUNNEBO SafePay SCL». Il difetto
                non era il filtro mancante: erano due campi per una cosa sola.
                Con uno, la contraddizione non e' piu' esprimibile.

                `marca` e `modello` restano nel database e si riempiono da qui:
                le legge l'export Excel. */}
            <DispositivoAutocomplete
              label="Dispositivo"
              placeholder="Digita marca, modello o sigla…"
              tipoDispositivo={watch("tipo_dispositivo") as TipoDispositivo | ""}
              value={watch("modello")}
              onChange={(v) => {
                setValue("modello", v);
                setValue("dispositivo_catalogo_id", "");
                setValue("marca", "");
              }}
              onSelect={(d) => {
                setValue("marca", d.marca);
                setValue("modello", `${d.marca} ${d.modello}`);
                setValue("dispositivo_catalogo_id", d.id);
              }}
            />
            {watch("dispositivo_catalogo_id") && <DispositivoMateriale dispositivoId={watch("dispositivo_catalogo_id")} />}
            {/* La matricola resta il valore SALVATO, come prima: il backend
                ricava `device_id` da qui per corrispondenza esatta, e questa
                rotta non ha mai accettato una chiave dal form. Il selettore
                non cambia quel percorso — riempie lo stesso campo con una
                matricola garantita esistente, cosi' la derivazione riesce
                invece di dipendere da come e' stata digitata.

                Resta scrivibile a mano: la matricola puo' riferirsi a un pezzo
                non ancora censito, e l'attivazione deve poter esistere lo
                stesso — e' scritto anche nel commento della rotta. */}
            <DeviceFisicoAutocomplete
              label="Matricola dispositivo"
              value={watch("dispositivo_matricola")}
              onChange={(v) => setValue("dispositivo_matricola", v)}
              deviceId={deviceSceltoId}
              onDeviceIdChange={setDeviceSceltoId}
              onSelect={(d) => {
                // Stessa regola di Installazioni, stessa funzione: scegliere il
                // pezzo riempie anche i due campi che lo descrivono, invece di
                // farli ridigitare accanto a una matricola che li conosce già.
                const { marca, modello, catalogoId } = modelloDaDevice(d);
                if (marca !== null) setValue("marca", marca);
                if (modello !== null) setValue("modello", modello);
                setValue("dispositivo_catalogo_id", catalogoId ?? "");
              }}
              sedeId={watch("sede_id") || null}
              hint={
                watch("sede_id")
                  ? "I pezzi che stanno in questa sede compaiono per primi."
                  : "Scegli prima la sede per vedere subito la cassaforte che sta lì."
              }
            />
            <CensimentoDevice
              matricola={watch("dispositivo_matricola")}
              deviceId={deviceSceltoId}
              censisci={censisciDevice}
              onCensisciChange={setCensisciDevice}
              tipoPossesso={possessoCensimento}
              onTipoPossessoChange={setPossessoCensimento}
            />

            <div className="grid grid-cols-2 gap-3">
              <Input label="Data programmata" type="date" {...register("data_attivazione")} />
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-ink">Prima attivazione / Riattivazione</label>
                <select
                  className="h-10 rounded-lg border border-black/10 bg-white px-3 text-sm text-ink"
                  {...register("tipo_attivazione")}
                >
                  {TIPI_ATTIVAZIONE.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Sotto la data programmata perché è quella a cui si riferisce:
                l'orario è dell'appuntamento, non dell'attivazione eseguita. */}
            <CampiOrario inizio={register("orario_inizio")} fine={register("orario_fine")} />

            <div className="flex flex-col gap-2 rounded-lg border border-black/10 bg-surface p-3">
              <label className="flex items-center gap-2 text-sm font-medium text-ink">
                <input type="checkbox" className="h-4 w-4 rounded border-black/20" {...register("effettuata")} />
                Effettuata
              </label>
              {isEffettuata && (
                <Input label="Data effettiva attivazione" type="date" {...register("data_effettiva")} />
              )}
            </div>

            <div className="border-t border-black/5 pt-4">
              <p className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">Vigilanza</p>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-ink">Istituto di Vigilanza</label>
                  <select
                    className="h-10 rounded-lg border border-black/10 bg-white px-3 text-sm text-ink"
                    {...register("istituto_vigilanza_id", {
                      onChange: (e) => {
                        const istituto = istituti.find((i) => i.id === e.target.value);
                        setValue("vigilanza", istituto?.nome ?? "");
                      },
                    })}
                  >
                    <option value="">—</option>
                    {istituti.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.nome}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-ink">Tipo servizio</label>
                  <select
                    className="h-10 rounded-lg border border-black/10 bg-white px-3 text-sm text-ink"
                    {...register("tipo_servizio")}
                  >
                    <option value="">—</option>
                    {TIPI_SERVIZIO_VIGILANZA.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                {isCongiunto && (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-ink">Centro Assistenza che interviene con la vigilanza</label>
                    <select
                      className="h-10 rounded-lg border border-black/10 bg-white px-3 text-sm text-ink"
                      {...register("centro_assistenza_vigilanza_id")}
                    >
                      <option value="">Seleziona centro…</option>
                      {centri.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {showComunicazione && (
              <div className="border-t border-black/5 pt-4">
                <p className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">Comunicazione</p>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-ink">Tipo comunicazione</label>
                  <select
                    className="h-10 rounded-lg border border-black/10 bg-white px-3 text-sm text-ink"
                    {...register("comunicazione_tipo")}
                  >
                    {COMUNICAZIONE_TIPI.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                {comunicazioneTipo === "LAN" && (
                  <div className="mt-3 flex flex-col gap-3">
                    <label className="flex items-center gap-2 text-sm font-medium text-ink">
                      <input type="checkbox" className="h-4 w-4 rounded border-black/20" {...register("lan_dhcp")} />
                      DHCP (indirizzo assegnato automaticamente dal router)
                    </label>
                    {!isDhcp && (
                      <div className="grid grid-cols-2 gap-3">
                        <Input label="Indirizzo IP" {...register("lan_ip")} />
                        <Input label="Subnet" {...register("lan_subnet")} />
                        <Input label="Gateway" {...register("lan_gateway")} />
                        {/* «DNS 1» e non più «DNS»: da solo era chiaro, in
                            coppia dire quale dei due è il primario è il minimo.
                            La colonna a database resta `lan_dns`. */}
                        <Input label="DNS 1" {...register("lan_dns")} />
                        <Input label="DNS 2" {...register("lan_dns2")} />
                      </div>
                    )}
                  </div>
                )}

                {comunicazioneTipo === "SIM" && (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <Input label="Numero SIM" {...register("sim_numero")} />
                    <Input label="Operatore" {...register("sim_operatore")} />
                  </div>
                )}
              </div>
            )}

            <div className="border-t border-black/5 pt-4">
              <Input label="Tecnico attivazione" {...register("tecnico_attivazione")} />
              <AssegnatarioSelect
                value={watch("assegnato_utente_id")}
                onChange={(id) => setValue("assegnato_utente_id", id)}
                cliente_id={watch("cliente_id")}
                centro_assistenza_id={
                  watch("centro_assistenza_id") || (modalita === "autonoma" ? centroDelChiamante : null)
                }
                istituto_vigilanza_id={watch("istituto_vigilanza_id")}
                aiuto="Chi segue questa attivazione. Il campo Tecnico sopra resta testo libero, per chi non ha un account."
              />

              <div className="mt-4 flex flex-col gap-1.5">
                <label className="text-sm font-medium text-ink">Stato</label>
                <select
                  className="h-10 rounded-lg border border-black/10 bg-white px-3 text-sm text-ink"
                  {...register("stato")}
                >
                  {STATI_ATTIVAZIONE.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-4 flex flex-col gap-1.5">
                <label className="text-sm font-medium text-ink">Note</label>
                <textarea
                  rows={3}
                  className="rounded-lg border border-black/10 bg-white px-3 py-2 text-sm text-ink"
                  {...register("note")}
                />
              </div>

              {/* Accanto a note e date, come sugli altri tre moduli: è un dato
                  amministrativo che si trascrive DOPO, non parte della
                  configurazione tecnica dell'attivazione. */}
              {puoGestireOrdini && (
                <div className="mt-4">
                  <RiferimentiOdoo
                    idPrefisso="attivazione"
                    ordini={editingRecord?.ordini_odoo}
                    valori={{
                      numero_oda: watch("numero_oda"),
                      oda_fattura_ricevuta: watch("oda_fattura_ricevuta"),
                      numero_odv: watch("numero_odv"),
                      odv_in_garanzia: watch("odv_in_garanzia"),
                      odv_fatturato: watch("odv_fatturato"),
                      spunte_toccate: watch("spunte_toccate"),
                    }}
                    onChange={(v) => {
                      setValue("numero_oda", v.numero_oda);
                      setValue("oda_fattura_ricevuta", v.oda_fattura_ricevuta);
                      setValue("numero_odv", v.numero_odv);
                      setValue("odv_in_garanzia", v.odv_in_garanzia);
                      setValue("odv_fatturato", v.odv_fatturato);
                      setValue("spunte_toccate", v.spunte_toccate);
                    }}
                  />
                </div>
              )}
            </div>
          </form>
        </fieldset>

        {editingRecord && (
          <div className="mt-6 border-t border-black/5 pt-4">
            <p className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">Foto e video</p>
            <MediaUploader entityType="attivazione" entityId={editingRecord.id} canEdit={canEdit} />
          </div>
        )}
      </SlideOver>
    </div>
  );
}
